/**
 * Base HTTP client for the Socaity web/platform API (`webBackendUrl`).
 * Domain subclasses attach route methods with an explicit auth requirement
 * (inspired by the frontend ``BackendService.add_endpoint`` pattern).
 */

import { config } from '../../core/config';
import { AuthRequirement, HttpClient } from '../http';

export interface QueryEnvelope<T = Record<string, any>> {
  ids: string[];
  entities: T[];
  fieldset: string;
  index_revision: string;
}

export interface CatalogQuery {
  q?: string;
  filters?: string[];
  expand?: string[];
  fields?: string[];
  sort?: string;
  limit?: number;
  offset?: number;
  known_ids?: string[];
  known_fieldset?: string;
  visibility?: string;
  mine?: boolean;
}

export interface EndpointSpec {
  method: string;
  auth: AuthRequirement;
}

export class BackendClient {
  protected readonly http: HttpClient;
  protected readonly baseUrl: string;

  /**
   * Route → method/auth. Subclasses call ``addEndpoint`` in their constructor.
   * Lookup falls back to ``default``.
   */
  protected readonly endpoints: Record<string, EndpointSpec> = {
    default: { method: 'GET', auth: 'required' },
  };

  constructor(apiKey?: string, webBackendUrl?: string) {
    this.http = new HttpClient(apiKey);
    this.baseUrl = (webBackendUrl ?? config.webBackendUrl).replace(/\/+$/, '');
  }

  /**
   * Declare auth/method for a route (same idea as frontend ``add_endpoint``).
   * ``auth``: ``required`` | ``optional`` | ``none``.
   */
  protected addEndpoint(route: string, method = 'GET', auth: AuthRequirement = 'required'): void {
    this.endpoints[route] = { method, auth };
  }

  protected endpointSpec(route: string): EndpointSpec {
    return this.endpoints[route] ?? this.endpoints.default;
  }

  protected url(path: string): string {
    const cleaned = path.replace(/^\/+/, '');
    return `${this.baseUrl}/${cleaned}`;
  }

  /** Low-level JSON request using the declared endpoint auth when ``auth`` is omitted. */
  protected async requestJson<T = any>(
    path: string,
    options: {
      method?: string;
      body?: any;
      query?: Record<string, any>;
      auth?: AuthRequirement;
      headers?: Record<string, string>;
    } = {},
  ): Promise<T> {
    const spec = this.endpointSpec(path);
    const method = options.method ?? spec.method;
    const auth = options.auth ?? spec.auth;
    const hasJsonBody = options.body !== undefined && options.body !== null;
    const response = await this.http.request(this.url(path), {
      method,
      query: options.query,
      body: hasJsonBody ? JSON.stringify(options.body) : null,
      headers: {
        Accept: 'application/json',
        ...(hasJsonBody ? { 'Content-Type': 'application/json' } : {}),
        ...options.headers,
      },
      auth,
    });
    if (response.status === 204) return undefined as T;
    const text = await response.text();
    if (!text) return undefined as T;
    return JSON.parse(text) as T;
  }

  protected async getJson<T = any>(
    path: string,
    query?: Record<string, any>,
    auth?: AuthRequirement,
  ): Promise<T> {
    return this.requestJson<T>(path, { method: 'GET', query, auth });
  }

  protected async postJson<T = any>(
    path: string,
    body?: any,
    query?: Record<string, any>,
    auth?: AuthRequirement,
  ): Promise<T> {
    return this.requestJson<T>(path, {
      method: 'POST',
      body: body === undefined ? null : (body ?? {}),
      query,
      auth,
    });
  }

  protected async putJson<T = any>(
    path: string,
    body?: any,
    query?: Record<string, any>,
    auth?: AuthRequirement,
  ): Promise<T> {
    return this.requestJson<T>(path, {
      method: 'PUT',
      body: body === undefined ? null : body,
      query,
      auth,
    });
  }

  protected async patchJson<T = any>(
    path: string,
    body?: any,
    query?: Record<string, any>,
    auth?: AuthRequirement,
  ): Promise<T> {
    return this.requestJson<T>(path, {
      method: 'PATCH',
      body: body === undefined ? null : body,
      query,
      auth,
    });
  }

  protected async deleteJson<T = any>(
    path: string,
    query?: Record<string, any>,
    auth?: AuthRequirement,
  ): Promise<T> {
    return this.requestJson<T>(path, { method: 'DELETE', query, auth });
  }

  /** Multipart POST (no Content-Type; browser sets the boundary). */
  protected async postForm<T = any>(
    path: string,
    formData: FormData,
    query?: Record<string, any>,
    auth: AuthRequirement = 'required',
  ): Promise<T> {
    const response = await this.http.request(this.url(path), {
      method: 'POST',
      query,
      body: formData,
      auth,
    });
    return (await response.json()) as T;
  }

  /** Flatten catalog-style query options into repeated FastAPI query keys. */
  protected catalogQuery(query: CatalogQuery = {}): Record<string, any> {
    return {
      q: query.q,
      filter: query.filters,
      expand: query.expand,
      fields: query.fields?.length ? query.fields.join(',') : undefined,
      sort: query.sort,
      limit: query.limit,
      offset: query.offset,
      known_ids: query.known_ids,
      known_fieldset: query.known_fieldset,
      visibility: query.visibility,
      mine: query.mine ? 'true' : undefined,
    };
  }

  /** Normalize bare arrays (legacy) or envelopes into a QueryEnvelope. */
  protected asEnvelope<T>(payload: any): QueryEnvelope<T> {
    if (Array.isArray(payload)) {
      const entities = payload as T[];
      return {
        ids: entities.map((e: any) => e?.id).filter(Boolean),
        entities,
        fieldset: '',
        index_revision: '0',
      };
    }
    return {
      ids: payload?.ids ?? [],
      entities: payload?.entities ?? [],
      fieldset: payload?.fieldset ?? '',
      index_revision: payload?.index_revision ?? '0',
    };
  }
}
